/**
 * CohortBuilder — reusable donor cohorts (major donors, board, lapsed, guest lists).
 *
 * Rebuilt in Phases 2–3 to match the training-cohort workflow it was meant to
 * mirror:
 *
 *  * the people picker searches CONTACTS AND MEMBERS — it used to hard-filter to
 *    `record_type='contact'`, which is why members who donate could not be added
 *    to a donor cohort at all;
 *  * membership carries a FUNNEL (candidate → selected → invited → accepted), so
 *    "invited" is a recorded state rather than an assumption;
 *  * cohorts can be built from SOURCES — a group, a campaign audience, an event
 *    roster, everyone who has given, all contacts — not just typed-in names;
 *  * inviting reports how many people were actually NOTIFIED, not how many rows
 *    were written;
 *  * create/edit are full-page forms rather than an inline text box.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Layers,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTabbedChrome } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import {
  CohortMember,
  CohortMemberStatus,
  CohortSource,
  CohortSourceType,
  DonorCohort,
  addCohortMembers,
  createCohort,
  deleteCohort,
  getCohort,
  inviteCohortToEvent,
  linkCohortSource,
  listCohortSources,
  listCohorts,
  previewCohortSource,
  removeCohortMember,
  setCohortMemberStatus,
  syncCohortSource,
  unlinkCohortSource,
  updateCohort,
} from '@/services/api/donorCohorts';
import { DonorEvent, listDonorEvents } from '@/services/api/donorEvents';
import DonorPeoplePicker from '@/components/donors/DonorPeoplePicker';

type View = 'list' | 'new' | 'detail';

const STATUS_VARIANT: Record<CohortMemberStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  candidate: 'outline',
  selected: 'default',
  invited: 'default',
  accepted: 'secondary',
  declined: 'destructive',
};

const SOURCES: { id: CohortSourceType; label: string; hint: string; needsRef: boolean }[] = [
  { id: 'donors', label: 'Everyone who has given', hint: 'All donors with a settled gift', needsRef: false },
  { id: 'contacts', label: 'All contacts', hint: 'Every contact record in the org', needsRef: false },
  { id: 'event', label: 'An event roster', hint: 'Everyone invited to a past event', needsRef: true },
  { id: 'group', label: 'A Communications group', hint: 'Reuses the group’s filters', needsRef: true },
  { id: 'campaign', label: 'A campaign audience', hint: 'Everyone who responded', needsRef: true },
];

function money(n?: number | null): string {
  return `$${Math.round(n || 0).toLocaleString('en-US')}`;
}

// --------------------------------------------------------------------------
// Sources panel
// --------------------------------------------------------------------------

function SourcesPanel({ cohortId, onChanged }: { cohortId: number; onChanged: () => void }) {
  const [sources, setSources] = useState<CohortSource[]>([]);
  const [type, setType] = useState<CohortSourceType>('donors');
  const [ref, setRef] = useState('');
  const [events, setEvents] = useState<DonorEvent[]>([]);
  const [preview, setPreview] = useState<{ resolved: number; would_add: number; label: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    listCohortSources(cohortId)
      .then(setSources)
      .catch(() => setSources([]));
  }, [cohortId]);

  useEffect(() => {
    load();
    listDonorEvents()
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [load]);

  const spec = SOURCES.find((s) => s.id === type)!;
  const canRun = !spec.needsRef || !!ref;

  const doPreview = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const p = await previewCohortSource(cohortId, { source_type: type, source_ref: ref || undefined });
      setPreview(p);
    } catch (e: any) {
      setMsg(e?.message || 'Could not preview that source.');
    } finally {
      setBusy(false);
    }
  };

  const doLink = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await linkCohortSource(cohortId, { source_type: type, source_ref: ref || undefined, auto_sync: true });
      setMsg(`Added ${r.added} donor${r.added === 1 ? '' : 's'} from ${r.label}.`);
      setPreview(null);
      setRef('');
      load();
      onChanged();
    } catch (e: any) {
      setMsg(e?.message || 'Could not add from that source.');
    } finally {
      setBusy(false);
    }
  };

  const field =
    'rounded border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Layers size={15} /> Build from a source
      </h3>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value as CohortSourceType);
            setRef('');
            setPreview(null);
          }}
          className={`${field} flex-1 min-w-[12rem]`}
        >
          {SOURCES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        {spec.needsRef && type === 'event' ? (
          <select value={ref} onChange={(e) => setRef(e.target.value)} className={`${field} flex-1 min-w-[12rem]`}>
            <option value="">Choose an event…</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
              </option>
            ))}
          </select>
        ) : spec.needsRef ? (
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder={`${spec.label} id`}
            className={`${field} flex-1 min-w-[12rem]`}
          />
        ) : null}
        <Button size="sm" variant="outline" disabled={!canRun || busy} onClick={doPreview}>
          Preview
        </Button>
        <Button size="sm" disabled={!canRun || busy} onClick={doLink}>
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Plus size={14} className="mr-1" />}
          Add
        </Button>
      </div>
      <p className="text-xs text-gray-400">{spec.hint}</p>

      {preview ? (
        <div className="rounded border border-blue-100 bg-blue-50/60 p-2 text-xs text-gray-700 dark:border-blue-900 dark:bg-blue-900/20 dark:text-gray-300">
          <strong>{preview.label}</strong> resolves to {preview.resolved} people — {preview.would_add} would be added.
        </div>
      ) : null}
      {msg ? <p className="text-xs text-gray-600 dark:text-gray-300">{msg}</p> : null}

      {sources.length > 0 ? (
        <div className="space-y-1 border-t border-gray-100 pt-2 dark:border-gray-700">
          {sources.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex min-w-0 items-center gap-1.5 text-gray-600 dark:text-gray-300">
                <Link2 size={12} className="shrink-0 text-gray-400" />
                <span className="truncate">{s.label}</span>
                {s.auto_sync ? <span className="text-gray-400">· auto-syncing</span> : null}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => syncCohortSource(cohortId, s.id).then(() => { load(); onChanged(); })}
                  className="rounded px-1.5 py-0.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Sync
                </button>
                <button
                  onClick={() => unlinkCohortSource(cohortId, s.id).then(load)}
                  className="rounded p-0.5 text-gray-300 hover:text-red-500"
                  title="Unlink (keeps the donors already added)"
                >
                  <X size={13} />
                </button>
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------------------
// Cohort detail
// --------------------------------------------------------------------------

function CohortDetail({
  cohortId,
  onBack,
  onChanged,
}: {
  cohortId: number;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [cohort, setCohort] = useState<DonorCohort | null>(null);
  const [events, setEvents] = useState<DonorEvent[]>([]);
  const [inviteEventId, setInviteEventId] = useState('');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const c = await getCohort(cohortId);
    setCohort(c);
    setName(c.name);
    setDescription(c.description || '');
  }, [cohortId]);

  useEffect(() => {
    load().catch((e: any) => setError(e?.message || 'Could not load the cohort.'));
    listDonorEvents(true)
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [load]);

  const invite = async () => {
    if (!inviteEventId) return;
    setInviting(true);
    setInviteMsg(null);
    try {
      const res = await inviteCohortToEvent(cohortId, Number(inviteEventId), { channels: ['email'] });
      setInviteMsg(
        `${res.invited} added to the guest list · ${res.notified} donor${res.notified === 1 ? '' : 's'} notified.`,
      );
      await load();
      onChanged();
    } catch (e: any) {
      setInviteMsg(e?.message || 'Could not invite the cohort.');
    } finally {
      setInviting(false);
    }
  };

  const saveDetails = async () => {
    try {
      await updateCohort(cohortId, { name: name.trim(), description: description.trim() });
      setEditing(false);
      await load();
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not save the cohort.');
    }
  };

  const cycleStatus = async (m: CohortMember) => {
    const order: CohortMemberStatus[] = ['candidate', 'selected', 'invited', 'accepted', 'declined'];
    const next = order[(order.indexOf(m.status) + 1) % order.length];
    await setCohortMemberStatus(cohortId, m.member_id, next);
    await load();
  };

  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <button onClick={onBack} className="mt-0.5 rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">
              {cohort?.name || 'Cohort'}
            </h1>
            {cohort?.description ? <p className="text-sm text-gray-500">{cohort.description}</p> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{cohort?.member_count ?? 0} donors</Badge>
          {cohort?.invited_count ? <Badge variant="outline">{cohort.invited_count} invited</Badge> : null}
          {cohort?.accepted_count ? <Badge variant="secondary">{cohort.accepted_count} accepted</Badge> : null}
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Rename'}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {editing ? (
        <div className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} placeholder="Cohort name" />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={field}
            placeholder="What this list is for"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={saveDetails} disabled={!name.trim()}>
              <Check size={14} className="mr-1" /> Save
            </Button>
          </div>
        </div>
      ) : null}

      {events.length > 0 ? (
        <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Invite this cohort to a donor event
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={inviteEventId}
              onChange={(e) => setInviteEventId(e.target.value)}
              className={`${field} flex-1 min-w-[12rem]`}
            >
              <option value="">Select an upcoming event…</option>
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
            <Button size="sm" disabled={!inviteEventId || inviting} onClick={invite}>
              {inviting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
              Invite all
            </Button>
          </div>
          {inviteMsg ? <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{inviteMsg}</p> : null}
        </div>
      ) : null}

      <SourcesPanel cohortId={cohortId} onChanged={() => { load(); onChanged(); }} />

      <div className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Add donors</h3>
        <DonorPeoplePicker
          excludeCohortId={cohortId}
          addLabel="Add to cohort"
          onAdd={async (people) => {
            await addCohortMembers(cohortId, people.map((p) => p.id));
            await load();
            onChanged();
          }}
        />
      </div>

      <div className="divide-y rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
        {cohort?.members && cohort.members.length > 0 ? (
          cohort.members.map((m) => (
            <div key={m.member_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{m.name}</span>
                  <button onClick={() => cycleStatus(m)} title="Click to advance the funnel">
                    <Badge variant={STATUS_VARIANT[m.status] || 'outline'}>{m.status}</Badge>
                  </button>
                  {m.record_type ? (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">{m.record_type}</span>
                  ) : null}
                </div>
                <div className="mt-0.5 truncate text-xs text-gray-400">
                  {m.email || m.phone || 'No contact details'}
                  {m.lifetime_giving ? ` · ${money(m.lifetime_giving)} lifetime` : ''}
                  {m.source_label ? ` · ${m.source_label}` : ''}
                </div>
              </div>
              <button
                onClick={() => removeCohortMember(cohortId, m.member_id).then(() => { load(); onChanged(); })}
                className="text-gray-300 hover:text-red-500"
                title="Remove from cohort"
              >
                <X size={16} />
              </button>
            </div>
          ))
        ) : (
          <div className="px-3 py-10 text-center text-sm text-gray-400">
            No donors yet — search above or build from a source.
          </div>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Main
// --------------------------------------------------------------------------

export const CohortBuilder: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [cohorts, setCohorts] = useState<DonorCohort[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setCohorts(await listCohorts());
    } catch (e: any) {
      setError(e?.message || 'Failed to load cohorts.');
      setCohorts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const c = await createCohort({ name: newName.trim(), description: newDescription.trim() || undefined });
      setNewName('');
      setNewDescription('');
      await load();
      setSelectedId(c.id);
      setView('detail');
    } catch (e: any) {
      setError(e?.message || 'Could not create the cohort.');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: number, name: string) => {
    if (!window.confirm(`Delete the cohort “${name}”? The donor records themselves are not affected.`)) return;
    await deleteCohort(id);
    if (selectedId === id) {
      setSelectedId(null);
      setView('list');
    }
    load();
  };

  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  if (view === 'detail' && selectedId != null) {
    return (
      <CohortDetail
        key={selectedId}
        cohortId={selectedId}
        onBack={() => {
          setView('list');
          setSelectedId(null);
          load();
        }}
        onChanged={load}
      />
    );
  }

  if (view === 'new') {
    return (
      <div className="mx-auto max-w-2xl space-y-5 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView('list')}
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">New donor cohort</h1>
            <p className="text-sm text-gray-500">A reusable list you can invite to any event.</p>
          </div>
        </div>
        {error ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        ) : null}
        <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={field}
              placeholder="Major donors, Board, Gala guest list…"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={3}
              className={field}
              placeholder="Who belongs on this list and why"
            />
          </div>
          <p className="text-xs text-gray-400">
            You can add donors by search or pull them in from a group, campaign, event roster, or your giving history
            once the cohort exists.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setView('list')}>
            Cancel
          </Button>
          <Button disabled={!newName.trim() || creating} onClick={create}>
            {creating ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Plus size={15} className="mr-1" />}
            Create cohort
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6 lg:p-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!tabbed && <Users size={26} className="text-blue-600" />}
          <div>
            {!tabbed && <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Donor cohorts</h1>}
            <p className="text-sm text-gray-500">Reusable donor lists — build once, invite to any event.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw size={15} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setView('new')}>
            <Plus size={15} className="mr-1" /> New cohort
          </Button>
        </div>
      </header>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}

      {cohorts === null ? (
        <div className="py-16 text-center text-gray-400">Loading cohorts…</div>
      ) : cohorts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
          <Users size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-600 dark:text-gray-300">No cohorts yet.</p>
          <p className="mt-1 text-sm text-gray-400">Create one to group donors and invite them together.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cohorts.map((c) => (
            <div
              key={c.id}
              className="cursor-pointer rounded-lg border border-gray-200 p-4 hover:shadow-md dark:border-gray-700"
              onClick={() => {
                setSelectedId(c.id);
                setView('detail');
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate font-medium text-gray-900 dark:text-gray-100">{c.name}</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(c.id, c.name);
                  }}
                  className="text-gray-300 hover:text-red-500"
                  title="Delete cohort"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {c.description ? <p className="mt-0.5 truncate text-xs text-gray-500">{c.description}</p> : null}
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                <span>{c.member_count} donors</span>
                {c.invited_count ? <span>· {c.invited_count} invited</span> : null}
                {c.accepted_count ? <span>· {c.accepted_count} accepted</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CohortBuilder;
